import { useEffect, useMemo, useState, useRef, useContext } from "react";
import api from "../../services/axiosConfig";
import { subscribeToInstances } from "../Home/Utils/GetAllInstancesAPI";
import { message } from "antd";
import { AuthContext } from "../../services/authcontext";

const parseInstancesData = (raw) => {
  const userInstances = raw?.user?.instances || raw?.instances || [];
  const userCollaborators =
    raw?.user?.collaborators || raw?.collaborators || [];
  const allRawInstances = [...userInstances, ...userCollaborators];

  const rawInstances = Array.from(
    new Map(
      allRawInstances.map((instance) => [instance.containerId, instance]),
    ).values(),
  );

  const instances = rawInstances.map((instance) => ({
    id: instance.containerId,
    label: instance.module?.label || "Unknown",
    name: instance.containerName,
    status: instance.status,
    moduleId: instance.module?.id,
    cpuUsage:
      instance.cpuUsageValue != null
        ? `${Number(instance.cpuUsageValue).toFixed(2)}%`
        : "N/A",
    cpuUsageValue: instance.cpuUsageValue ?? 0,
    memoryUsage:
      instance.memoryUsageValue != null
        ? `${Number(instance.memoryUsageValue).toFixed(2)} MB`
        : "N/A",
    memoryUsageValue: instance.memoryUsageValue ?? 0,
    diskUsageValue: instance.diskUsageValue ?? 0,
    diskVirtualUsageValue: instance.diskVirtualUsageValue ?? 0,
    diskUsage: instance.diskUsage || "N/A",
    ports: instance.exposedPorts
      ? instance.exposedPorts.split(",").map((p) => p.trim())
      : [],

    isTotal: false,
  }));

  instances.sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  if (instances.length === 0) return [];

  const totalCpu =
    raw?.totalCpuUsage ?? instances.reduce((s, i) => s + i.cpuUsageValue, 0);
  const totalMemory =
    raw?.totalMemoryUsage ??
    instances.reduce((s, i) => s + i.memoryUsageValue, 0);
  const totalDisk =
    raw?.totalDiskUsage ?? instances.reduce((s, i) => s + i.diskUsageValue, 0);
  const totalVirtualDisk =
    raw?.totalVirtualDiskUsage ??
    instances.reduce((s, i) => s + (i.diskVirtualUsageValue || 0), 0);
  const totalDiskDisplay =
    totalVirtualDisk > 0
      ? `${Number(totalDisk).toFixed(2)} GB (virtual ${Number(totalVirtualDisk).toFixed(2)} GB)`
      : "N/A";

  const totalRow = {
    id: "TOTAL_ROW",
    label: "Total Containers Usage",
    name: "Total Usage",
    status: "SUMMARY",
    moduleId: null,
    cpuUsage: `${Number(totalCpu).toFixed(2)}%`,
    cpuUsageValue: totalCpu,
    memoryUsage: `${Number(totalMemory).toFixed(2)} MB`,
    memoryUsageValue: totalMemory,
    diskUsageValue: totalDisk,
    diskUsage: totalDiskDisplay,
    ports: [],
    isTotal: true,
  };

  return [totalRow, ...instances];
};

export default function useInstancesStream() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isBackendDown, setIsBackendDown] = useState(false);

  // 1. Initialize to TRUE so we don't instantly panic and poll before WS connects
  const [wsConnected, setWsConnected] = useState(true);

  const pollingIntervalRef = useRef(null);
  const fallbackTimeoutRef = useRef(null);
  const { user } = useContext(AuthContext);
  const isRoleUser =
    user?.roles?.some((role) => role.name === "ROLE_USER") ?? false;

  useEffect(() => {
    const userData = user;
    const userId = userData?.id;
    const activated = new Set(
      (userData?.activatedModules || []).map((m) => m.label),
    );

    const onMessage = (rows) => {
      const filtered = rows.filter((r) => r.isTotal || activated.has(r.label));
      setData(filtered);
      setLoading(false);
      setError(false);
    };

    const cleanup = subscribeToInstances(userId, onMessage, (isConnected) => {
      setWsConnected(isConnected);
      if (isConnected) {
        setIsBackendDown((prev) => {
          if (prev) {
            message.success({
              content:
                "Connection Restored: Successfully connected to the backend.",
              key: "connection-status",
            });
          }
          return false;
        });
      } else {
        // <-- ADD THIS: Automatically flip loader to true so it spins when restored!
        setLoading(true);
      }
    });

    return cleanup;
  }, [user]);

  useEffect(() => {
    const userData = user;
    const activated = new Set(
      (userData?.activatedModules || []).map((m) => m.label),
    );

    if (!wsConnected) {
      const fetchApi = async () => {
        try {
          const response = await api.get("/instances", {
            headers: { Accept: "application/json" },
          });

          const parsedData = parseInstancesData(response.data.data);
          const filtered = parsedData.filter(
            (r) => r.isTotal || activated.has(r.label),
          );

          setData(filtered);
          setLoading(false);
          setError(false);
          setIsBackendDown((prev) => {
            if (prev) {
              message.success({
                content:
                  "Connection Restored: Successfully connected to the backend.",
                key: "connection-status",
              });
            }
            return false;
          });
        } catch (err) {
          console.error("API Fallback failed", err);
          setData([]);
          setError(true);
          setIsBackendDown(true);
          // <-- ADD THIS: Ensure HTTP polling fallback failures queue the loader to run upon rescue
          setLoading(true);
          message.error({
            content: "Live update failed, retrying...",
            key: "connection-status",
          });
        }
      };

      fallbackTimeoutRef.current = setTimeout(() => {
        fetchApi();
        pollingIntervalRef.current = setInterval(fetchApi, 5000);
      }, 1500);
    } else {
      if (fallbackTimeoutRef.current) clearTimeout(fallbackTimeoutRef.current);
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    }

    return () => {
      if (fallbackTimeoutRef.current) clearTimeout(fallbackTimeoutRef.current);
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    };
  }, [wsConnected, user]);

  const modules = useMemo(
    () => Array.from(new Set(data.map((d) => d.label))).filter(Boolean),
    [data],
  );

  return { data, loading, error, modules, isBackendDown };
}
